// src/pages/trading-portal/VolatilityArbitrage/OpportunityDetectionPage.js
// Opportunity Detection page with 3-tab interface: Scan Configs, Detected Opportunities, VRP Monitor

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Switch,
    FormControlLabel,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    OpportunityType, OpportunityTypeLabels, OpportunityTypeColors,
    OpportunityStatus, OpportunityStatusLabels, OpportunityStatusColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const bodyCellSx = {
    fontFamily,
    fontSize: '0.82rem',
    color: '#334155',
};

const OpportunityDetectionPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ==================== TAB 0: SCAN CONFIGS ====================
    const [scanConfigs, setScanConfigs] = useState([]);
    const [scansLoading, setScansLoading] = useState(true);
    const [scansPage, setScansPage] = useState(0);
    const [scansPerPage, setScansPerPage] = useState(10);
    const [scansTotal, setScansTotal] = useState(0);
    const [runningScanId, setRunningScanId] = useState(null);

    // ==================== TAB 1: DETECTED OPPORTUNITIES ====================
    const [opportunities, setOpportunities] = useState([]);
    const [oppsLoading, setOppsLoading] = useState(false);
    const [oppsPage, setOppsPage] = useState(0);
    const [oppsPerPage, setOppsPerPage] = useState(10);
    const [oppsTotal, setOppsTotal] = useState(0);
    const [oppsStatusFilter, setOppsStatusFilter] = useState('');
    const [oppsTypeFilter, setOppsTypeFilter] = useState('');
    const [oppsSymbolSearch, setOppsSymbolSearch] = useState('');

    // ==================== DIALOGS ====================
    // Add/Edit Scan Config Dialog
    const [scanDialog, setScanDialog] = useState(false);
    const [scanEditing, setScanEditing] = useState(null);
    const [scanForm, setScanForm] = useState({
        symbol: '',
        scan_type: '',
        z_score_threshold: '',
        lookback_days: '',
        is_enabled: true,
    });

    // Delete Confirmation Dialog
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // ==================== FORMAT HELPERS ====================
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';
    const formatNumber = (num, decimals = 2) => num != null ? parseFloat(num).toFixed(decimals) : '-';

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, add, edit, del] = await Promise.all([
                hasPermission('tr-vae-opportunity-detection', 'view'),
                hasPermission('tr-vae-opportunity-detection', 'add'),
                hasPermission('tr-vae-opportunity-detection', 'edit'),
                hasPermission('tr-vae-opportunity-detection', 'delete'),
            ]);
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPerms();
    }, []);

    // ==================== TAB 0: SCAN CONFIGS ====================
    const fetchScanConfigs = useCallback(async () => {
        if (!canView) return;
        setScansLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: scansPage + 1, per_page: scansPerPage });
            const res = await axiosInstance.get(`/api/volatility-arbitrage/scans?${params.toString()}`);
            if (res.data.success) {
                setScanConfigs(res.data.data || []);
                setScansTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch scan configs');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch scan configs');
        } finally {
            setScansLoading(false);
        }
    }, [canView, scansPage, scansPerPage]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchScanConfigs();
    }, [fetchScanConfigs, canView, activeTab]);

    // ==================== TAB 1: DETECTED OPPORTUNITIES ====================
    const fetchOpportunities = useCallback(async () => {
        if (!canView) return;
        setOppsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: oppsPage + 1, per_page: oppsPerPage });
            if (oppsStatusFilter) params.append('status', oppsStatusFilter);
            if (oppsTypeFilter) params.append('opportunity_type', oppsTypeFilter);
            if (oppsSymbolSearch) params.append('symbol', oppsSymbolSearch);
            const res = await axiosInstance.get(`/api/volatility-arbitrage/opportunities?${params.toString()}`);
            if (res.data.success) {
                setOpportunities(res.data.data || []);
                setOppsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch opportunities');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch opportunities');
        } finally {
            setOppsLoading(false);
        }
    }, [canView, oppsPage, oppsPerPage, oppsStatusFilter, oppsTypeFilter, oppsSymbolSearch]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchOpportunities();
    }, [fetchOpportunities, canView, activeTab]);

    // ==================== SCAN CONFIG CRUD ====================
    const openAddScanDialog = () => {
        setScanEditing(null);
        setScanForm({ symbol: '', scan_type: '', z_score_threshold: '', lookback_days: '', is_enabled: true });
        setScanDialog(true);
    };

    const openEditScanDialog = (scan) => {
        setScanEditing(scan);
        setScanForm({
            symbol: scan.symbol || '',
            scan_type: scan.scan_type || '',
            z_score_threshold: scan.z_score_threshold != null ? String(scan.z_score_threshold) : '',
            lookback_days: scan.lookback_days != null ? String(scan.lookback_days) : '',
            is_enabled: scan.is_enabled ?? true,
        });
        setScanDialog(true);
    };

    const handleScanFormChange = (field, value) => {
        setScanForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveScan = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                symbol: scanForm.symbol,
                scan_type: scanForm.scan_type,
                z_score_threshold: scanForm.z_score_threshold ? parseFloat(scanForm.z_score_threshold) : null,
                lookback_days: scanForm.lookback_days ? parseInt(scanForm.lookback_days, 10) : null,
                is_enabled: scanForm.is_enabled,
            };
            let res;
            if (scanEditing) {
                res = await axiosInstance.put(`/api/volatility-arbitrage/scans/${scanEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/volatility-arbitrage/scans', payload);
            }
            if (res.data.success) {
                setSnackbar({ open: true, message: scanEditing ? 'Scan config updated' : 'Scan config created', severity: 'success' });
                setScanDialog(false);
                fetchScanConfigs();
            } else {
                setError(res.data.message || 'Failed to save scan config');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save scan config');
        } finally {
            setSubmitting(false);
        }
    };

    const openDeleteDialog = (scan) => {
        setDeleteTarget(scan);
        setDeleteDialog(true);
    };

    const handleDeleteScan = async () => {
        if (!deleteTarget) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await axiosInstance.delete(`/api/volatility-arbitrage/scans/${deleteTarget.id}`);
            if (res.data.success) {
                setSnackbar({ open: true, message: 'Scan config deleted', severity: 'success' });
                setDeleteDialog(false);
                setDeleteTarget(null);
                fetchScanConfigs();
            } else {
                setError(res.data.message || 'Failed to delete scan config');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete scan config');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRunScan = async (scanId) => {
        setRunningScanId(scanId);
        try {
            const res = await axiosInstance.post(`/api/volatility-arbitrage/scans/${scanId}/run`);
            if (res.data.success) {
                setSnackbar({ open: true, message: 'Scan triggered successfully', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to run scan', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to run scan', severity: 'error' });
        } finally {
            setRunningScanId(null);
        }
    };

    // ==================== RENDER: SKELETON ROWS ====================
    const renderSkeletonRows = (colCount, rowCount = 5) => (
        Array.from({ length: rowCount }).map((_, i) => (
            <TableRow key={`skel-${i}`}>
                {Array.from({ length: colCount }).map((__, j) => (
                    <TableCell key={j}>
                        <Skeleton variant="text" sx={{ fontFamily }} />
                    </TableCell>
                ))}
            </TableRow>
        ))
    );

    // ==================== RENDER: TAB 0 - SCAN CONFIGS ====================
    const renderScanConfigs = () => (
        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                    Scan Configurations
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={fetchScanConfigs} sx={{ color: '#64748b' }}>
                            <MuiIcons.Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {canAdd && (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<MuiIcons.Add />}
                            onClick={openAddScanDialog}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                bgcolor: ACCENT,
                                '&:hover': { bgcolor: '#d97706' },
                                borderRadius: '8px',
                                boxShadow: 'none',
                            }}
                        >
                            Add Scan
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Table */}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCellSx}>Symbol</TableCell>
                            <TableCell sx={headCellSx}>Scan Type</TableCell>
                            <TableCell sx={headCellSx}>Z-Score Threshold</TableCell>
                            <TableCell sx={headCellSx}>Lookback Days</TableCell>
                            <TableCell sx={headCellSx}>Enabled</TableCell>
                            <TableCell sx={headCellSx}>Last Scan</TableCell>
                            <TableCell sx={headCellSx} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scansLoading ? renderSkeletonRows(7) : scanConfigs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ ...bodyCellSx, py: 4, color: '#94a3b8' }}>
                                    No scan configurations found. Click "Add Scan" to create one.
                                </TableCell>
                            </TableRow>
                        ) : scanConfigs.map((scan) => (
                            <TableRow key={scan.id} hover sx={{ '&:hover': { bgcolor: '#fffbeb' } }}>
                                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{scan.symbol || '-'}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Chip
                                        label={OpportunityTypeLabels[scan.scan_type] || scan.scan_type}
                                        color={OpportunityTypeColors[scan.scan_type] || 'default'}
                                        size="small"
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem' }}
                                    />
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{formatNumber(scan.z_score_threshold, 2)}</TableCell>
                                <TableCell sx={bodyCellSx}>{scan.lookback_days != null ? scan.lookback_days : '-'}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Chip
                                        label={scan.is_enabled ? 'Enabled' : 'Disabled'}
                                        color={scan.is_enabled ? 'success' : 'default'}
                                        size="small"
                                        variant={scan.is_enabled ? 'filled' : 'outlined'}
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem' }}
                                    />
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{formatDateTime(scan.last_scan_at)}</TableCell>
                                <TableCell sx={bodyCellSx} align="right">
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                        <Tooltip title="Run Scan">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRunScan(scan.id)}
                                                disabled={runningScanId === scan.id}
                                                sx={{ color: '#10b981' }}
                                            >
                                                <MuiIcons.PlayArrow fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {canEdit && (
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEditScanDialog(scan)} sx={{ color: '#3b82f6' }}>
                                                    <MuiIcons.Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canDelete && (
                                            <Tooltip title="Delete">
                                                <IconButton size="small" onClick={() => openDeleteDialog(scan)} sx={{ color: '#ef4444' }}>
                                                    <MuiIcons.Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={scansTotal}
                page={scansPage}
                onPageChange={(_, p) => setScansPage(p)}
                rowsPerPage={scansPerPage}
                onRowsPerPageChange={(e) => { setScansPerPage(parseInt(e.target.value, 10)); setScansPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
            />
        </Paper>
    );

    // ==================== RENDER: TAB 1 - DETECTED OPPORTUNITIES ====================
    const renderOpportunities = () => (
        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            {/* Toolbar with Filters */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                    Detected Opportunities
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Symbol..."
                        value={oppsSymbolSearch}
                        onChange={(e) => { setOppsSymbolSearch(e.target.value); setOppsPage(0); }}
                        sx={{ width: 120, '& .MuiInputBase-input': { fontFamily, fontSize: '0.82rem' } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.82rem' }}>Status</InputLabel>
                        <Select
                            value={oppsStatusFilter}
                            label="Status"
                            onChange={(e) => { setOppsStatusFilter(e.target.value); setOppsPage(0); }}
                            sx={{ fontFamily, fontSize: '0.82rem' }}
                        >
                            <MenuItem value="" sx={{ fontFamily, fontSize: '0.82rem' }}>All</MenuItem>
                            {Object.entries(OpportunityStatus).map(([key, val]) => (
                                <MenuItem key={key} value={val} sx={{ fontFamily, fontSize: '0.82rem' }}>
                                    {OpportunityStatusLabels[key]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.82rem' }}>Type</InputLabel>
                        <Select
                            value={oppsTypeFilter}
                            label="Type"
                            onChange={(e) => { setOppsTypeFilter(e.target.value); setOppsPage(0); }}
                            sx={{ fontFamily, fontSize: '0.82rem' }}
                        >
                            <MenuItem value="" sx={{ fontFamily, fontSize: '0.82rem' }}>All</MenuItem>
                            {Object.entries(OpportunityType).map(([key, val]) => (
                                <MenuItem key={key} value={val} sx={{ fontFamily, fontSize: '0.82rem' }}>
                                    {OpportunityTypeLabels[key]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={fetchOpportunities} sx={{ color: '#64748b' }}>
                            <MuiIcons.Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCellSx}>Symbol</TableCell>
                            <TableCell sx={headCellSx}>Type</TableCell>
                            <TableCell sx={headCellSx}>IV</TableCell>
                            <TableCell sx={headCellSx}>Predicted RV</TableCell>
                            <TableCell sx={headCellSx}>Z-Score</TableCell>
                            <TableCell sx={headCellSx}>Spread (bps)</TableCell>
                            <TableCell sx={headCellSx}>Confidence (%)</TableCell>
                            <TableCell sx={headCellSx}>Direction</TableCell>
                            <TableCell sx={headCellSx}>Status</TableCell>
                            <TableCell sx={headCellSx}>Detected At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {oppsLoading ? renderSkeletonRows(10) : opportunities.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ ...bodyCellSx, py: 4, color: '#94a3b8' }}>
                                    No opportunities detected yet. Configure and run scans to detect opportunities.
                                </TableCell>
                            </TableRow>
                        ) : opportunities.map((opp) => (
                            <TableRow key={opp.id} hover sx={{ '&:hover': { bgcolor: '#fffbeb' } }}>
                                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{opp.symbol || '-'}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Chip
                                        label={OpportunityTypeLabels[opp.opportunity_type] || opp.opportunity_type}
                                        color={OpportunityTypeColors[opp.opportunity_type] || 'default'}
                                        size="small"
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem' }}
                                    />
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{formatNumber(opp.implied_volatility, 4)}</TableCell>
                                <TableCell sx={bodyCellSx}>{formatNumber(opp.predicted_rv, 4)}</TableCell>
                                <TableCell sx={bodyCellSx}>{formatNumber(opp.z_score, 2)}</TableCell>
                                <TableCell sx={bodyCellSx}>{formatNumber(opp.spread_bps, 2)}</TableCell>
                                <TableCell sx={bodyCellSx}>{opp.confidence != null ? `${formatNumber(opp.confidence, 1)}%` : '-'}</TableCell>
                                <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: opp.direction === 'LONG' ? '#10b981' : opp.direction === 'SHORT' ? '#ef4444' : '#64748b' }}>
                                    {opp.direction || '-'}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Chip
                                        label={OpportunityStatusLabels[opp.status] || opp.status}
                                        color={OpportunityStatusColors[opp.status] || 'default'}
                                        size="small"
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem' }}
                                    />
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{formatDateTime(opp.detected_at)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={oppsTotal}
                page={oppsPage}
                onPageChange={(_, p) => setOppsPage(p)}
                rowsPerPage={oppsPerPage}
                onRowsPerPageChange={(e) => { setOppsPerPage(parseInt(e.target.value, 10)); setOppsPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
            />
        </Paper>
    );

    // ==================== RENDER: TAB 2 - VRP MONITOR ====================
    const renderVRPMonitor = () => (
        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 4, textAlign: 'center' }}>
            <MuiIcons.MonitorHeart sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem', mb: 1 }}>
                VRP Monitor
            </Typography>
            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                VRP monitoring data will appear here when scans are configured.
            </Typography>
        </Paper>
    );

    // ==================== RENDER: SCAN CONFIG DIALOG ====================
    const renderScanDialog = () => (
        <Dialog
            open={scanDialog}
            onClose={() => setScanDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
                {scanEditing ? 'Edit Scan Config' : 'Add Scan Config'}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <TextField
                    label="Symbol"
                    value={scanForm.symbol}
                    onChange={(e) => handleScanFormChange('symbol', e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ '& .MuiInputBase-input': { fontFamily }, '& .MuiInputLabel-root': { fontFamily } }}
                />
                <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontFamily }}>Scan Type</InputLabel>
                    <Select
                        value={scanForm.scan_type}
                        label="Scan Type"
                        onChange={(e) => handleScanFormChange('scan_type', e.target.value)}
                        sx={{ fontFamily }}
                    >
                        {Object.entries(OpportunityType).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>
                                {OpportunityTypeLabels[key]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Z-Score Threshold"
                    type="number"
                    value={scanForm.z_score_threshold}
                    onChange={(e) => handleScanFormChange('z_score_threshold', e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: '0.1' }}
                    sx={{ '& .MuiInputBase-input': { fontFamily }, '& .MuiInputLabel-root': { fontFamily } }}
                />
                <TextField
                    label="Lookback Days"
                    type="number"
                    value={scanForm.lookback_days}
                    onChange={(e) => handleScanFormChange('lookback_days', e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: '1', min: '1' }}
                    sx={{ '& .MuiInputBase-input': { fontFamily }, '& .MuiInputLabel-root': { fontFamily } }}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={scanForm.is_enabled}
                            onChange={(e) => handleScanFormChange('is_enabled', e.target.checked)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                        />
                    }
                    label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Enabled</Typography>}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setScanDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSaveScan}
                    variant="contained"
                    disabled={submitting || !scanForm.symbol || !scanForm.scan_type}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        bgcolor: ACCENT,
                        '&:hover': { bgcolor: '#d97706' },
                        borderRadius: '8px',
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Saving...' : scanEditing ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: DELETE DIALOG ====================
    const renderDeleteDialog = () => (
        <Dialog
            open={deleteDialog}
            onClose={() => setDeleteDialog(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                Delete Scan Config
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.85rem' }}>
                    Are you sure you want to delete the scan config for <strong>{deleteTarget?.symbol}</strong> ({OpportunityTypeLabels[deleteTarget?.scan_type] || deleteTarget?.scan_type})? This action cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleDeleteScan}
                    variant="contained"
                    disabled={submitting}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        bgcolor: '#ef4444',
                        '&:hover': { bgcolor: '#dc2626' },
                        borderRadius: '8px',
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== MAIN RENDER ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>
                    You do not have permission to view opportunity detection. Required permission: <strong>tr-vae-opportunity-detection</strong>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <MuiIcons.TravelExplore sx={{ fontSize: 28, color: ACCENT }} />
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '1.4rem', lineHeight: 1.2 }}>
                        Opportunity Detection
                    </Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                        Configure volatility scans, detect arbitrage opportunities, and monitor the volatility risk premium
                    </Typography>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newVal) => setActiveTab(newVal)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            minHeight: 48,
                            color: '#64748b',
                            '&.Mui-selected': { color: ACCENT },
                        },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px 3px 0 0' },
                    }}
                >
                    <Tab icon={<MuiIcons.Settings sx={{ fontSize: 18 }} />} iconPosition="start" label="Scan Configs" />
                    <Tab icon={<MuiIcons.Lightbulb sx={{ fontSize: 18 }} />} iconPosition="start" label="Detected Opportunities" />
                    <Tab icon={<MuiIcons.MonitorHeart sx={{ fontSize: 18 }} />} iconPosition="start" label="VRP Monitor" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderScanConfigs()}
            {activeTab === 1 && renderOpportunities()}
            {activeTab === 2 && renderVRPMonitor()}

            {/* Dialogs */}
            {renderScanDialog()}
            {renderDeleteDialog()}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ fontFamily, fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OpportunityDetectionPage;
